import { useEffect, useState, useCallback } from 'react';
import { syncService, SyncStatus } from '../services/syncService';
import { useAuth } from './useAuth';
import { Idea } from '../services/api';

export const useSync = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('disconnected');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const { token } = useAuth();

  useEffect(() => {
    if (token) {
      // Connect to sync service
      syncService.connect(token);

      // Subscribe to status changes
      const unsubscribe = syncService.on('statusChanged', (status: SyncStatus) => {
        setSyncStatus(status);
        if (status === 'connected') {
          setLastSyncTime(new Date());
        }
      });

      return () => {
        unsubscribe();
        syncService.disconnect();
      };
    }
  }, [token]);

  const subscribeToIdeas = useCallback((
    onCreated: (idea: Idea) => void,
    onUpdated: (idea: Idea) => void,
    onDeleted: (id: string) => void
  ) => {
    const unsubscribeCreated = syncService.on('ideaCreated', onCreated);
    const unsubscribeUpdated = syncService.on('ideaUpdated', onUpdated);
    const unsubscribeDeleted = syncService.on('ideaDeleted', onDeleted);

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeDeleted();
    };
  }, []);

  const subscribeToProjects = useCallback((onCreated: (project: any) => void) => {
    return syncService.on('projectCreated', onCreated);
  }, []);

  return {
    syncStatus,
    lastSyncTime,
    subscribeToIdeas,
    subscribeToProjects,
    isConnected: syncStatus === 'connected',
    isSyncing: syncStatus === 'syncing'
  };
};